import React, { useState, useEffect } from "react";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import {
  FileText,
  MapPin,
  Calendar,
  Printer,
  FileDown,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { format } from "date-fns";
import { useAuth } from "../../auth/AuthContext";
import { fetchXReport, printXReport } from "../../services/reportsApi";
import { useToast } from "../../hooks/useToast";

interface XReportData {
  businessInfo: {
    name: string;
    address: string;
    reportStart: string;
    reportEnd: string;
  };
  salesAndTaxes: {
    numberOfSales: number;
    totalNetSale: number;
    totalTax: number;
    totalSales: number;
    totalQuickPayments: number;
  };
  tender: {
    items: { method: string; in: number; out: number; amount: number }[];
    totalTendered: number;
  };
  salesByService: {
    items: { name: string; quantity: number; amount: number; canceled: boolean }[];
    totalNet: number;
    total: number;
  };
  salesByProduct: {
    items: { name: string; quantity: number; amount: number; canceled: boolean }[];
    totalNet: number;
    total: number;
  };
  salesBySunbed: {
    items: { name: string; amount: number; canceled: boolean }[];
    totalNet: number;
    total: number;
  };
  salesByEmployee: {
    items: { name: string; amount: number; totalSales: number }[];
    total: number;
    totalSalesCount: number;
  };
  commissionsByEmployee: {
    items: { name: string; commission: number }[];
    total: number;
  };
  refundsAndDiscounts: {
    refundsNet: number;
    refundsTaxGross: number;
    totalDiscount: number;
    totalRefunds: number;
  };
}

const CurrencyDisplay = ({
  amount,
  bold = false,
}: {
  amount: number | string | null | undefined;
  bold?: boolean;
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const value = Number(amount);
  const display = Number.isFinite(value) ? value.toFixed(2) : "0.00";

  return (
    <span
      className={`${bold ? "font-bold text-gray-900" : "font-medium text-gray-700"
        } `}
    >
      {currencySymbol}{display}
    </span>
  );
};

const SectionHeader = ({ title }: { title: string }) => (
  <div className="border-b-2 border-[#885ABB]/20 pb-2 mb-4 mt-6">
    <h3 className="text-[#885ABB] font-bold text-sm tracking-uppercase uppercase tracking-wider">
      {title}
    </h3>
  </div>
);

const RowItem = ({
  label,
  value,
  bold = false,
  subLabel = "",
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
  subLabel?: string;
}) => (
  <div
    className={`flex justify-between items-center py-2 ${bold ? "border-t border-gray-100 mt-1 pt-3" : ""
      } `}
  >
    <div className="flex flex-col">
      <span
        className={`${bold ? "font-bold text-gray-900" : "text-gray-600 font-medium"
          } text-sm`}
      >
        {label}
      </span>
      {subLabel && <span className="text-xs text-gray-400">{subLabel}</span>}
    </div>
    {value}
  </div>
);

// Helper function to extract quantity from product name (e.g., "Home Spa Kit (1)" -> 1)
const extractQuantity = (name: string): number => {
  const match = name.match(/\((\d+)\)$/);
  return match ? parseInt(match[1], 10) : 1;
};

// Helper function to remove quantity from product name
const cleanProductName = (name: string): string => {
  return name.replace(/\s*\(\d+\)$/, '');
};

const XReport: React.FC = () => {
  const [data, setData] = useState<XReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<"print" | "download" | null>(null);
  const toast = useToast();

  const handlePrint = async () => {
    setPdfLoading("print");
    try {
      const blob = await printXReport();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (w) w.onload = () => URL.revokeObjectURL(url);
      else URL.revokeObjectURL(url);
      toast.success("Report opened in new tab. Use the browser print button to print.");
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? "Failed to load PDF";
      toast.error(msg);
    } finally {
      setPdfLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading("download");
    try {
      const blob = await printXReport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `X-Report-${format(new Date(), "yyyy-MM-dd-HHmm")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded.");
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? "Failed to download PDF";
      toast.error(msg);
    } finally {
      setPdfLoading(null);
    }
  };

  useEffect(() => {
    const loadXReport = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchXReport();
        const report = response.report;

        // Transform API response to component data structure (API may return numbers as strings)
        const companyInfo = report.companyInfo[0] || { name: "N/A", address: "N/A" };
        const toNum = (v: unknown) => {
          if (typeof v === "number") return Number.isFinite(v) ? v : 0;
          if (typeof v === "string") return Number(String(v).replace(/,/g, "")) || 0;
          return 0;
        };

        // Salonify: totalSaleAmount is NET; Total Sales = net + tax
        const totalSaleAmount = toNum(report.totalSaleAmount);
        const totalTaxAmount = toNum(report.totalTaxAmount);
        const totalNetSale = totalSaleAmount;
        const totalSales = totalSaleAmount + totalTaxAmount;

        const tenders = report.tenders;
        const tenderItems = [
          {
            method: "Cash",
            in: toNum(tenders.cashIn),
            out: toNum(tenders.cashOut),
            amount: toNum(tenders.cashIn) - toNum(tenders.cashOut),
          },
          {
            method: "Card",
            in: toNum(tenders.cardIn),
            out: toNum(tenders.cardOut),
            amount: toNum(tenders.cardIn) - toNum(tenders.cardOut),
          },
          {
            method: "Cheque",
            in: toNum(tenders.chequeIn),
            out: toNum(tenders.chequeOut),
            amount: toNum(tenders.chequeIn) - toNum(tenders.chequeOut),
          },
        ];

        const mapLineItems = (
          items: Array<{ name: string; amount: unknown; status?: string }> = []
        ) =>
          items.map((item) => ({
            name: cleanProductName(item.name),
            quantity: extractQuantity(item.name),
            amount: toNum(item.amount),
            canceled: String(item.status || "").toLowerCase() === "canceled",
          }));

        const serviceItems = mapLineItems(report.byService || []);
        const productItems = mapLineItems(report.byProducts || []);
        const sunbedItems = (report.bySunbed || []).map((item) => ({
          name: item.name,
          amount: toNum(item.amount),
          canceled: String(item.status || "").toLowerCase() === "canceled",
        }));

        const sumActive = (items: { amount: number; canceled: boolean }[]) =>
          items.reduce((sum, item) => (item.canceled ? sum : sum + item.amount), 0);

        const employeeItems = (report.byEmployee || []).map((item) => ({
          name: item.name,
          amount: toNum(item.amount),
          totalSales: Number(item.total_sales) || 0,
        }));

        const commissionItems = (report.byEmployee || []).map((item) => ({
          name: item.name,
          commission: toNum(item.commission),
        }));

        const transformedData: XReportData = {
          businessInfo: {
            name: companyInfo.name,
            address: companyInfo.address,
            reportStart: report.begin,
            reportEnd: report.end,
          },
          salesAndTaxes: {
            numberOfSales: toNum(report.noOfSales),
            totalNetSale,
            totalTax: totalTaxAmount,
            totalSales,
            totalQuickPayments: toNum(report.totalQuickPayment),
          },
          tender: {
            items: tenderItems,
            totalTendered: toNum(tenders.totalTenderAmount),
          },
          salesByService: {
            items: serviceItems,
            totalNet: toNum(report.servicesTotalAmount),
            total: sumActive(serviceItems),
          },
          salesByProduct: {
            items: productItems,
            totalNet: toNum(report.productsTotalAmount),
            total: sumActive(productItems),
          },
          salesBySunbed: {
            items: sunbedItems,
            totalNet: toNum(report.sunbedTotalAmount),
            total: sumActive(sunbedItems),
          },
          salesByEmployee: {
            items: employeeItems,
            total: employeeItems.reduce((sum, emp) => sum + emp.amount, 0),
            totalSalesCount: employeeItems.reduce((sum, emp) => sum + emp.totalSales, 0),
          },
          commissionsByEmployee: {
            items: commissionItems,
            total: commissionItems.reduce((sum, emp) => sum + emp.commission, 0),
          },
          refundsAndDiscounts: {
            refundsNet: toNum(report.totalCanceledAmount),
            refundsTaxGross: toNum(report.canceledTotalTaxAmount),
            totalDiscount: toNum(report.totalDiscount),
            totalRefunds: toNum(report.totalCanceledAmount) + toNum(report.canceledTotalTaxAmount),
          },
        };

        setData(transformedData);
      } catch (err) {
        console.error("Failed to fetch X report:", err);
        setError("Failed to load X report");
      } finally {
        setIsLoading(false);
      }
    };

    loadXReport();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-red-500 text-lg">{error || "Report not found"}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col space-y-4 lg:space-y-6 p-4 lg:p-6">
      {/* Page Header */}
      <SubPageHeader
        title="X Report"
        subtitle="Snapshot of daily sales activity"
        icon={FileText}
        actions={
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="h-10 lg:h-9 bg-white hover:bg-gray-50 text-gray-700 border-gray-200 rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm"
              onClick={handlePrint}
              disabled={!!pdfLoading}
            >
              <Printer size={16} className="mr-1.5 lg:mr-2" />
              <span>{pdfLoading === "print" ? "Opening…" : "Print"}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-10 lg:h-9 bg-white hover:bg-gray-50 text-gray-700 border-gray-200 rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm"
              onClick={handleDownloadPdf}
              disabled={!!pdfLoading}
            >
              <FileDown size={16} className="mr-1.5 lg:mr-2" />
              <span>{pdfLoading === "download" ? "Downloading…" : "Download PDF"}</span>
            </Button>
          </div>
        }
      />

      {/* Report Content - Centered Card */}
      <div className="flex-1 flex justify-center pb-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-3xl w-full p-6 sm:p-8 md:p-12 relative overflow-hidden">
          {/* Decorative Top Accent */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#885ABB]"></div>

          {/* Header */}
          <div className="text-center mb-6 lg:mb-8 pb-6 border-b border-gray-100">
            <h1 className="text-xl lg:text-2xl font-black text-[#402B69] mb-2 tracking-tight">
              X Report
            </h1>
            <h2 className="text-base lg:text-lg font-bold text-[#885ABB] mb-1">
              {data.businessInfo.name}
            </h2>
            <div className="flex items-center justify-center gap-1 text-gray-400 text-[10px] lg:text-sm mb-4">
              <MapPin size={12} className="opacity-70" />
              <span className="font-medium">{data.businessInfo.address}</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-purple-50 text-[#885ABB] text-[10px] lg:text-xs font-bold px-4 py-2 rounded-full border border-purple-100/50">
              <Calendar size={12} />
              <div className="flex flex-col sm:flex-row sm:gap-1 text-center">
                <span>Report run from:</span>
                <span className="font-black">
                  {data.businessInfo.reportStart} - {data.businessInfo.reportEnd}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Section 1: Sales and Taxes */}
            <div>
              <SectionHeader title="Sales and Taxes Summary" />
              <div className="space-y-1">
                <RowItem
                  label="Number of Sales"
                  value={
                    <span className="text-gray-900 font-bold">
                      {data.salesAndTaxes.numberOfSales}
                    </span>
                  }
                />
                <RowItem
                  label="Total Net Sale"
                  value={
                    <CurrencyDisplay amount={data.salesAndTaxes.totalNetSale} />
                  }
                />
                <RowItem
                  label="Total Tax"
                  value={<CurrencyDisplay amount={data.salesAndTaxes.totalTax} />}
                />
                <RowItem
                  label="Total Sales"
                  value={
                    <CurrencyDisplay amount={data.salesAndTaxes.totalSales} bold />
                  }
                  bold
                />
                <RowItem
                  label="Total Quick Payments"
                  value={
                    <CurrencyDisplay
                      amount={data.salesAndTaxes.totalQuickPayments}
                    />
                  }
                />
              </div>
            </div>

            {/* Section 2: Tender */}
            <div>
              <SectionHeader title="Tender" />
              <div className="w-full">
                <div className="hidden sm:grid grid-cols-4 gap-4 text-[10px] lg:text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3 px-2 border-b border-gray-50 pb-2">
                  <div className="col-span-1">Tender</div>
                  <div className="col-span-1 text-right">In</div>
                  <div className="col-span-1 text-right">Out</div>
                  <div className="col-span-1 text-right">Amount</div>
                </div>
                <div className="space-y-3 sm:space-y-2">
                  {data.tender.items.map((item, idx) => (
                    <div key={idx} className="flex flex-col sm:grid sm:grid-cols-4 gap-2 sm:gap-4 py-2 sm:py-1 px-2 bg-gray-50/50 sm:bg-transparent rounded-xl sm:rounded-none">
                      <div className="flex justify-between sm:block border-b sm:border-0 border-gray-100 pb-2 sm:pb-0">
                        <span className="sm:hidden text-[10px] uppercase text-gray-400 font-bold">Method</span>
                        <span className="font-bold sm:font-medium text-[#402B69] sm:text-gray-700">{item.method}</span>
                      </div>
                      <div className="flex justify-between sm:block">
                        <span className="sm:hidden text-[10px] uppercase text-gray-400 font-bold">In</span>
                        <div className="sm:text-right text-gray-600 font-medium">
                          <CurrencyDisplay amount={item.in} />
                        </div>
                      </div>
                      <div className="flex justify-between sm:block">
                        <span className="sm:hidden text-[10px] uppercase text-gray-400 font-bold">Out</span>
                        <div className="sm:text-right text-gray-600 font-medium">
                          <CurrencyDisplay amount={item.out} />
                        </div>
                      </div>
                      <div className="flex justify-between sm:block">
                        <span className="sm:hidden text-[10px] uppercase text-gray-400 font-bold">Amount</span>
                        <div className="sm:text-right font-bold text-[#885ABB]">
                          <CurrencyDisplay amount={item.amount} bold />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-4 lg:pt-3 mt-2 border-t border-gray-100 px-2 group">
                    <span className="font-bold text-[#402B69] uppercase text-xs tracking-wider">Total Tendered</span>
                    <CurrencyDisplay amount={data.tender.totalTendered} bold />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Sales By Service */}
            <div>
              <SectionHeader title="Sales By Service" />
              <div className="space-y-1">
                {data.salesByService.items.length > 0 ? (
                  data.salesByService.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 px-2 hover:bg-gray-50/50 rounded-lg transition-colors group">
                      <span className={`font-medium text-sm pr-4 ${item.canceled ? "text-gray-400 line-through" : "text-gray-700"}`}>
                        {item.name}
                        <span className="text-xs text-[#885ABB] bg-purple-50 px-2 py-0.5 rounded-full ml-2 font-bold whitespace-nowrap">
                          x{item.quantity}
                        </span>
                        {item.canceled && (
                          <span className="ml-2 text-[10px] uppercase text-red-500 font-bold no-underline">Canceled</span>
                        )}
                      </span>
                      <span className={item.canceled ? "text-gray-400" : undefined}>
                        {item.canceled ? "- " : ""}
                        <CurrencyDisplay amount={item.amount} />
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic px-2">No service sales</p>
                )}
                <div className="mt-3 space-y-1">
                  <RowItem
                    label="Total Net"
                    value={
                      <CurrencyDisplay amount={data.salesByService.totalNet} bold />
                    }
                    bold
                  />
                  <div className="flex justify-between items-center py-2 px-2 pt-3 border-t border-gray-50">
                    <span className="font-bold text-[#402B69] text-sm uppercase tracking-wider">Total</span>
                    <CurrencyDisplay amount={data.salesByService.total} bold />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Sales By Product */}
            <div>
              <SectionHeader title="Sales By Product" />
              <div className="space-y-1">
                {data.salesByProduct.items.length > 0 ? (
                  data.salesByProduct.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 px-2 hover:bg-gray-50/50 rounded-lg transition-colors group">
                      <span className={`font-medium text-sm pr-4 ${item.canceled ? "text-gray-400 line-through" : "text-gray-700"}`}>
                        {item.name}
                        <span className="text-xs text-[#885ABB] bg-purple-50 px-2 py-0.5 rounded-full ml-2 font-bold whitespace-nowrap">
                          x{item.quantity}
                        </span>
                        {item.canceled && (
                          <span className="ml-2 text-[10px] uppercase text-red-500 font-bold no-underline">Canceled</span>
                        )}
                      </span>
                      <span className={item.canceled ? "text-gray-400" : undefined}>
                        {item.canceled ? "- " : ""}
                        <CurrencyDisplay amount={item.amount} />
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic px-2">No product sales</p>
                )}
                <div className="mt-3 space-y-1">
                  <RowItem
                    label="Total Net"
                    value={
                      <CurrencyDisplay amount={data.salesByProduct.totalNet} bold />
                    }
                    bold
                  />
                  <div className="flex justify-between items-center py-2 px-2 pt-3 border-t border-gray-50">
                    <span className="font-bold text-[#402B69] text-sm uppercase tracking-wider">Total</span>
                    <CurrencyDisplay amount={data.salesByProduct.total} bold />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: Sales By Sunbed */}
            <div>
              <SectionHeader title="Sales By Sunbed" />
              <div className="space-y-1">
                {data.salesBySunbed.items.length > 0 ? (
                  data.salesBySunbed.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 px-2 hover:bg-gray-50/50 rounded-lg transition-colors">
                      <span className={`font-medium text-sm pr-4 ${item.canceled ? "text-gray-400 line-through" : "text-gray-700"}`}>
                        {item.name}
                        {item.canceled && (
                          <span className="ml-2 text-[10px] uppercase text-red-500 font-bold no-underline">Canceled</span>
                        )}
                      </span>
                      <span className={item.canceled ? "text-gray-400" : undefined}>
                        {item.canceled ? "- " : ""}
                        <CurrencyDisplay amount={item.amount} />
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic px-2">No sunbed sales</p>
                )}
                <div className="mt-3 space-y-1">
                  <RowItem
                    label="Total Net"
                    value={<CurrencyDisplay amount={data.salesBySunbed.totalNet} bold />}
                    bold
                  />
                  <div className="flex justify-between items-center py-2 px-2 pt-3 border-t border-gray-50">
                    <span className="font-bold text-[#402B69] text-sm uppercase tracking-wider">Total</span>
                    <CurrencyDisplay amount={data.salesBySunbed.total} bold />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Sales By Employee */}
            <div>
              <SectionHeader title="Sales By Employee" />
              <div className="space-y-1">
                {data.salesByEmployee.items.map((emp, idx) => (
                  <RowItem
                    key={idx}
                    label={emp.name}
                    value={
                      <span className="text-sm font-medium text-gray-700">
                        <CurrencyDisplay amount={emp.amount} />
                        {emp.totalSales > 0 && (
                          <span className="ml-1 text-xs text-gray-400">(x{emp.totalSales})</span>
                        )}
                      </span>
                    }
                  />
                ))}
                <RowItem
                  label="Total"
                  value={
                    <span className="text-sm font-bold text-gray-900">
                      <CurrencyDisplay amount={data.salesByEmployee.total} bold />
                      {data.salesByEmployee.totalSalesCount > 0 && (
                        <span className="ml-1 text-xs text-gray-400">
                          (x{data.salesByEmployee.totalSalesCount})
                        </span>
                      )}
                    </span>
                  }
                  bold
                />
              </div>
            </div>

            {/* Section 7: Commissions By Employee */}
            <div>
              <SectionHeader title="Commissions By Employee" />
              <div className="space-y-1">
                {data.commissionsByEmployee.items.length > 0 ? (
                  data.commissionsByEmployee.items.map((emp, idx) => (
                    <RowItem
                      key={idx}
                      label={emp.name}
                      value={<CurrencyDisplay amount={emp.commission} />}
                    />
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic px-2">No commissions</p>
                )}
                <RowItem
                  label="Total"
                  value={<CurrencyDisplay amount={data.commissionsByEmployee.total} bold />}
                  bold
                />
              </div>
            </div>

            {/* Section 8: Refunds & Discounts */}
            <div className="pb-4">
              <SectionHeader title="Refunds & Discounts Summary" />
              <div className="space-y-1">
                <RowItem
                  label="Total Refunds Net"
                  value={
                    <CurrencyDisplay amount={data.refundsAndDiscounts.refundsNet} />
                  }
                />
                <RowItem
                  label="Total Refunds Tax Gross"
                  value={
                    <CurrencyDisplay
                      amount={data.refundsAndDiscounts.refundsTaxGross}
                    />
                  }
                />
                <RowItem
                  label="Total Discount"
                  value={
                    <CurrencyDisplay
                      amount={data.refundsAndDiscounts.totalDiscount}
                    />
                  }
                />
                <RowItem
                  label="Total Refunds"
                  value={
                    <div className="text-[#EF4444]">
                      <CurrencyDisplay
                        amount={data.refundsAndDiscounts.totalRefunds}
                        bold
                      />
                    </div>
                  }
                  bold
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XReport;
